import {
    bcbs_config_attribute,
    bcbs_config_attribute_bcbs_config_secti,
    bcbs_config_category,
    bcbs_config_masterattribute,
    bcbs_config_row,
    bcbs_config_section,
    bcbs_config_tab
} from "./CRM";
import { IGroupConfigurationConfig } from "./FlexibleAttributeModel";

export interface IFlexibleFormDefinition {
    tabs: IFlexibleFormTabDefinition[];
}

export interface IFlexibleFormCategoryDefinition extends IFlexibleFormDefinition {
    category: bcbs_config_category;
}

export interface IFlexibleFormTabDefinition {
    tab: bcbs_config_tab;
    sections: IFlexibleFormSectionDefinition[];
}

export interface IFlexibleFormSectionDefinition {
    section: bcbs_config_section;
    rows: IFlexibleFormRowDefinition[];
    attributes: IFlexibleFormAttributeDefinition[];
}

export interface IFlexibleFormRowDefinition {
    row: bcbs_config_row;
    attributes: IFlexibleFormAttributeDefinition[];
}

export interface IFlexibleFormAttributeDefinition {
    // category: bcbs_config_category;
    // tab: bcbs_config_tab;
    // section: bcbs_config_section;
    // row: bcbs_config_row;
    attribute: bcbs_config_attribute;
    masterAttribute: bcbs_config_masterattribute;
}

export interface IFlexibleFormAttributeSetDefinition extends IFlexibleFormDefinition {
    attributeSet: bcbs_config_masterattribute;
    attribute: bcbs_config_attribute;
}

export interface IFlexibleFormNonStandardDefinition extends IFlexibleFormDefinition {
    section: bcbs_config_section;
}

// TODO Tempo. adding attributes to section until rows are populated
/**
 * We use this to prune the Form definition of empty tabs, sections, and rows.
 * @param flexibleFormDefinition the form definition
 */
function pruneEmptyFormDefinitionElements(flexibleFormDefinition: IFlexibleFormDefinition): void {
    if (!flexibleFormDefinition)
        return;

    for (let i = flexibleFormDefinition.tabs.length - 1; i >= 0; i--) {
        let tab = flexibleFormDefinition.tabs[i];

        if (tab.tab.bcbs_isalwaysvisible)
            continue;

        // check if the tab is empty
        if (tab.sections.length === 0) {
            flexibleFormDefinition.tabs.splice(i, 1);
            continue;
        }

        for (let j = tab.sections.length - 1; j >= 0; j--) {
            let section = tab.sections[j];

            if (section.section.bcbs_isalwaysvisible)
                continue;

            // check if the section is empty
            if (section.rows.length === 0) {
                tab.sections.splice(j, 1);
                continue;
            }

            for (let k = section.rows.length - 1; k >= 0; k--) {
                let row = section.rows[k];

                // check if the row is empty
                if (row.attributes.length === 0) {
                    section.rows.splice(k, 1);
                    continue;
                }
            }


            // once we process all the rows of the section
            // we need to recheck the row count
            if (section.rows.length === 0) {
                tab.sections.splice(j, 1);
                continue;
            }
        }

        // once we process all the sections of the tab
        // we need to recheck the section count
        if (tab.sections.length === 0) {
            flexibleFormDefinition.tabs.splice(i, 1);
            continue;
        }
    }
}

/**
 * We use this to prune the Form definition of empty tabs, sections, and rows.
 * @param flexibleFormDefinition the form definition
 */
function pruneEmptyFormDefinitionElements_no_rows(flexibleFormDefinition: IFlexibleFormDefinition): void {
    if (!flexibleFormDefinition)
        return;

    for (let i = flexibleFormDefinition.tabs.length - 1; i >= 0; i--) {
        let tab = flexibleFormDefinition.tabs[i];

        if (tab.tab.bcbs_isalwaysvisible)
            continue;

        // check if the tab is empty
        if (tab.sections.length === 0) {
            flexibleFormDefinition.tabs.splice(i, 1);
            continue;
        }

        for (let j = tab.sections.length - 1; j >= 0; j--) {
            let section = tab.sections[j];

            if (section.section.bcbs_isalwaysvisible)
                continue;

            // check if the section is empty
            if (section.attributes.length === 0) {
                tab.sections.splice(j, 1);
                continue;
            }
        }

        // once we process all the sections of the tab
        // we need to recheck the section count
        if (tab.sections.length === 0) {
            flexibleFormDefinition.tabs.splice(i, 1);
            continue;
        }
    }
}

/**
 * Builds the Form Definition for all categories. The form definition defines all the tabs,
 * all the tab's sections, all the tab's section's rows, and all the tab's section's row's attributes.
 * This structure can be used to generate the UI or to generate the fields for a given category.
 * @param release the release configuration data
 * @returns A mapping of category id to the category form definition
 */
export function buildCategoryFormDefinitions(release: IGroupConfigurationConfig): Record<string, IFlexibleFormCategoryDefinition> {
    let categories: Record<string, IFlexibleFormCategoryDefinition> = {};

    let maMap = new Map<string, bcbs_config_masterattribute>();
    release.masterattributes.forEach(ma => {
        maMap.set(ma.bcbs_config_masterattributeid!, ma);
    });

    release.categories.forEach(cat => {
        let uicat: IFlexibleFormCategoryDefinition = {
            category: cat,
            tabs: []
        };

        categories[cat.bcbs_config_categoryid!] = uicat;
    });

    let tabMap = new Map<string, IFlexibleFormTabDefinition>();
    release.tabs.forEach(tab => {
        if (!tab || !tab._bcbs_config_categoryid_value)
            return;

        let cat = categories[tab._bcbs_config_categoryid_value];
        if (!cat)
            return;

        let uitab: IFlexibleFormTabDefinition = {
            tab: tab,
            sections: []
        };

        cat.tabs.push(uitab);
        tabMap.set(tab.bcbs_config_tabid!, uitab);
    });

    let sectionMap = new Map<string, IFlexibleFormSectionDefinition>();
    release.sections.forEach(section => {
        if (!section || !section._bcbs_config_tabid_value)
            return;

        let tab = tabMap.get(section._bcbs_config_tabid_value);
        if (!tab)
            return;

        let uisection: IFlexibleFormSectionDefinition = {
            section: section,
            rows: [],
            attributes: []
        };

        tab.sections.push(uisection);
        sectionMap.set(section.bcbs_config_sectionid!, uisection);
    });

    let rowMap = new Map<string, IFlexibleFormRowDefinition>();
    release.rows.forEach(row => {
        if (!row || !row._bcbs_config_sectionid_value)
            return;

        let section = sectionMap.get(row._bcbs_config_sectionid_value);
        if (!section)
            return;

        let uirow: IFlexibleFormRowDefinition = {
            row: row,
            attributes: []
        }

        section.rows.push(uirow);
        rowMap.set(row.bcbs_config_rowid!, uirow);
    });

    release.attributes.forEach(attr => {

        if (!attr)
            return;

        // skip attribute set attributes
        if (attr._bcbs_masterattributesetid_value)
            return;

        // skip non-standard attributes
        if (attr.bcbs_nonstandard)
            return;

        if (!attr._bcbs_config_masterattributeid_value)
            return;

        // // make sure it's for the current row
        if (!attr._bcbs_config_rowid_value)
            return;

        let row = rowMap.get(attr._bcbs_config_rowid_value);
        if (!row)
            return;

        // TODO Tempo. adding attributes to section until rows are populated
        if (!attr._bcbs_config_sectionid_value)
            return;

        // TODO Tempo. adding attributes to section until rows are populated
        let section = sectionMap.get(attr._bcbs_config_sectionid_value);
        if (!section)
            return;

        let mattr = maMap.get(attr._bcbs_config_masterattributeid_value);

        if (!mattr)
            return;

        let uiattr: IFlexibleFormAttributeDefinition = {
            attribute: attr,
            // category: cat,
            masterAttribute: mattr,
            // row: row, 
            // section: section,
            // tab: tab
        };

        row.attributes.push(uiattr);
    });

    for (let key in categories) {
        let category = categories[key];
        pruneEmptyFormDefinitionElements(category);
    }

    for (let key in categories) {
        let category = categories[key];
        category.tabs.forEach(tab => {
            tab.sections.forEach(section => {
                if (section.attributes.length > 1) {
                    throw new Error("Section attributes should be empty");
                }

                section.rows.forEach(row => {
                    row.attributes.forEach(attr => {
                        section.attributes.push(attr);
                    })
                });
            })
        })
    }

    return categories;
}

/**
 * Builds the Form Definition for all attribute sets. The form definition defines all the tabs,
 * all the tab's sections, all the tab's section's rows, and all the tab's section's row's attributes.
 * This structure can be used to generate the UI or to generate the fields for a given attribute set.
 * @param release the release configuration data
 * @returns A mapping of attribute set master attribute id to the attribute set form definition
 */
export function buildAttributeSetFormDefinitions(release: IGroupConfigurationConfig): Record<string, IFlexibleFormAttributeSetDefinition> {
    let maMap = new Map<string, bcbs_config_masterattribute>();
    release.masterattributes.forEach(ma => {
        maMap.set(ma.bcbs_config_masterattributeid!, ma);
    });

    let categoryMap = new Map<string, bcbs_config_category>();
    release.categories.forEach(cat => {
        categoryMap.set(cat.bcbs_config_categoryid!, cat);
    });

    let attributeSetIds: string[] = [];
    let attributeSets: Record<string, IFlexibleFormAttributeSetDefinition> = {};

    // build the attribute set lookup
    release.attributes.forEach(attr => {
        if (!attr)
            return;

        if (!attr._bcbs_config_masterattributeid_value)
            return;

        let mattr = maMap.get(attr._bcbs_config_masterattributeid_value);

        if (!mattr)
            return;

        // skip attributes that are NOT attribute sets
        if (mattr.bcbs_attributetypecode !== 808900015)
            return;

        attributeSetIds.push(attr._bcbs_config_masterattributeid_value);
        attributeSets[attr._bcbs_config_masterattributeid_value] = {
            attribute: attr,
            attributeSet: mattr,
            tabs: []
        };
    });

    // now, for each attributeset, we need to determine the UI hierarchy
    attributeSetIds.forEach(attrsetid => {
        let tabMap = new Map<string, IFlexibleFormTabDefinition>();
        release.tabs.forEach(tab => {
            if (!tab || !tab._bcbs_config_categoryid_value)
                return;

            let uitab: IFlexibleFormTabDefinition = {
                tab: tab,
                sections: []
            };

            attributeSets[attrsetid].tabs.push(uitab);
            tabMap.set(tab.bcbs_config_tabid!, uitab);
        });

        let sectionMap = new Map<string, IFlexibleFormSectionDefinition>();
        release.sections.forEach(section => {
            if (!section || !section._bcbs_config_tabid_value)
                return;

            let tab = tabMap.get(section._bcbs_config_tabid_value);
            if (!tab)
                return;

            let uisection: IFlexibleFormSectionDefinition = {
                section: section,
                rows: [],
                attributes: []
            };

            tab.sections.push(uisection);
            sectionMap.set(section.bcbs_config_sectionid!, uisection);
        });

        let rowMap = new Map<string, IFlexibleFormRowDefinition>();
        release.rows.forEach(row => {
            if (!row || !row._bcbs_config_sectionid_value)
                return;

            let section = sectionMap.get(row._bcbs_config_sectionid_value);
            if (!section)
                return;

            let uirow: IFlexibleFormRowDefinition = {
                row: row,
                attributes: []
            }

            section.rows.push(uirow);
            rowMap.set(row.bcbs_config_rowid!, uirow);
        });

        release.attributes.forEach(attr => {
            if (!attr)
                return;

            // make sure attribute belongs to this attribute set 
            if (attr._bcbs_masterattributesetid_value !== attrsetid)
                return;

            // make sure it has a row reference
            if (!attr._bcbs_config_rowid_value)
                return;

            // skip non-attribute set attributes
            if (!attr._bcbs_masterattributesetid_value)
                return;

            // skip non-standard attributes
            if (attr.bcbs_nonstandard)
                return;

            if (!attr._bcbs_config_masterattributeid_value)
                return;

            let row = rowMap.get(attr._bcbs_config_rowid_value);
            if (!row)
                return;


            // TODO Tempo. adding attributes to section until rows are populated
            if (!attr._bcbs_config_sectionid_value)
                return;

            // TODO Tempo. adding attributes to section until rows are populated
            let section = sectionMap.get(attr._bcbs_config_sectionid_value);
            if (!section)
                return;


            let mattr = maMap.get(attr._bcbs_config_masterattributeid_value);

            if (!mattr)
                return;

            let uiattr: IFlexibleFormAttributeDefinition = {
                attribute: attr,
                // category: cat,
                masterAttribute: mattr,
                // row: row, 
                // section: section,
                // tab: tab
            };

            row.attributes.push(uiattr);
        });

        pruneEmptyFormDefinitionElements(attributeSets[attrsetid]);
    });


    for (let key in attributeSets) {
        let nonStandard = attributeSets[key];
        nonStandard.tabs.forEach(tab => {
            tab.sections.forEach(section => {
                if (section.attributes.length > 1) {
                    throw new Error("Section attributes should be empty");
                }

                section.rows.forEach(row => {
                    row.attributes.forEach(attr => {
                        section.attributes.push(attr);
                    })
                });
            })
        })
    }

    return attributeSets;
}

/**
 * Builds the Form Definition for all non-standards. The form definition defines all the tabs,
 * all the tab's sections, all the tab's section's rows, and all the tab's section's row's attributes.
 * This structure can be used to generate the UI or to generate the fields for a non-standard on a given section.
 * @param config the release configuration data
 * @returns A mapping of section id to the non-standard form definition
 */
export function buildNonStandardFormDefinitions(config: IGroupConfigurationConfig): Record<string, IFlexibleFormNonStandardDefinition> {
    let maMap = new Map<string, bcbs_config_masterattribute>();
    config.masterattributes.forEach(ma => {
        maMap.set(ma.bcbs_config_masterattributeid!, ma);
    });

    let categoryMap = new Map<string, bcbs_config_category>();
    config.categories.forEach(cat => {
        categoryMap.set(cat.bcbs_config_categoryid!, cat);
    });

    // non-standards are defined at the section level
    let sections = new Map<string, bcbs_config_section>();

    // iterate all sections and grab those that are marked enable non-standards
    config.sections.forEach((section) => {
        if (!section)
            return;

        if (!section.bcbs_isenablenonstandard)
            return;

        sections.set(section.bcbs_config_sectionid!, section);
    });

    // create a map of sectionid+attributeid
    // we'll use this later to make sure that we're adding the right fields
    // to the correct sections
    let fieldSections = new Map<string, bcbs_config_attribute_bcbs_config_secti>();
    config.attributeNonStandardSections.forEach(a => {
        if (!a || !a.bcbs_config_sectionid || !a.bcbs_config_attributeid)
            return;

        fieldSections.set(`${a.bcbs_config_sectionid}-${a.bcbs_config_attributeid}`, a);
    });

    let nonStandards: Record<string, IFlexibleFormNonStandardDefinition> = {};

    sections.forEach(nonStandardSection => {
        let tabs: IFlexibleFormTabDefinition[] = [];

        let tabMap = new Map<string, IFlexibleFormTabDefinition>();
        config.tabs.forEach(tab => {
            if (!tab || !tab._bcbs_config_categoryid_value)
                return;

            let uitab: IFlexibleFormTabDefinition = {
                tab: tab,
                sections: []
            };

            tabs.push(uitab);
            tabMap.set(tab.bcbs_config_tabid!, uitab);
        });

        let sectionMap = new Map<string, IFlexibleFormSectionDefinition>();
        config.sections.forEach(section => {
            if (!section || !section._bcbs_config_tabid_value)
                return;

            let tab = tabMap.get(section._bcbs_config_tabid_value);
            if (!tab)
                return;

            let uisection: IFlexibleFormSectionDefinition = {
                section: section,
                rows: [],
                attributes: []
            };

            tab.sections.push(uisection);
            sectionMap.set(section.bcbs_config_sectionid!, uisection);
        });

        let rowMap = new Map<string, IFlexibleFormRowDefinition>();
        config.rows.forEach(row => {
            if (!row || !row._bcbs_config_sectionid_value)
                return;

            let section = sectionMap.get(row._bcbs_config_sectionid_value);
            if (!section)
                return;

            let uirow: IFlexibleFormRowDefinition = {
                row: row,
                attributes: []
            }

            section.rows.push(uirow);
            rowMap.set(row.bcbs_config_rowid!, uirow);
        });

        config.attributes.forEach(attr => {
            if (!attr)
                return;

            if (!attr._bcbs_config_rowid_value)
                return;

            // skip attribute set attributes
            if (attr._bcbs_masterattributesetid_value)
                return;

            if (!attr._bcbs_config_masterattributeid_value)
                return;

            // check if the field is for the section
            let combo = fieldSections.get(`${nonStandardSection.bcbs_config_sectionid}-${attr.bcbs_config_attributeid}`);
            if (!combo)
                return;

            let row = rowMap.get(attr._bcbs_config_rowid_value);
            if (!row)
                return;

            // TODO Tempo. adding attributes to section until rows are populated
            if (!attr._bcbs_config_sectionid_value)
                return;

            // TODO Tempo. adding attributes to section until rows are populated
            let section = sectionMap.get(attr._bcbs_config_sectionid_value);
            if (!section)
                return;

            let mattr = maMap.get(attr._bcbs_config_masterattributeid_value);

            if (!mattr)
                return;

            let uiattr: IFlexibleFormAttributeDefinition = {
                attribute: attr,
                // category: cat,
                masterAttribute: mattr,

                // row: row, 
                // section: section,
                // tab: tab
            };

            row.attributes.push(uiattr);
        });

        let definition: IFlexibleFormNonStandardDefinition = {
            section: nonStandardSection,
            tabs: tabs
        };

        pruneEmptyFormDefinitionElements(definition);

        nonStandards[nonStandardSection.bcbs_config_sectionid!] = definition;
    });


    for (let key in nonStandards) {
        let nonStandard = nonStandards[key];
        nonStandard.tabs.forEach(tab => {
            tab.sections.forEach(section => {
                if (section.attributes.length > 1) {
                    throw new Error("Section attributes should be empty");
                }

                section.rows.forEach(row => {
                    row.attributes.forEach(attr => {
                        section.attributes.push(attr);
                    })
                });
            })
        })
    }

    return nonStandards;
}